import { createSlice } from '@reduxjs/toolkit'
import {
  colorSpec,
  colorSpecGradiant,
  colorSpecGradiantColors,
  colorSpecGradiantNums,
  colorSpecNumBucket,
  colorSpecStrBucket,
  deckOptsColorsIn,
  deckOptsConstants,
  deckOptsConstantsBools,
  deckOptsConstantsNums,
  deckOptsProps,
  fixedColor,
  layerProp,
} from '../types.ts'
import { dispatchType as DT } from '../../../reduxhooks.ts'
const initialState = [] as layerProp[]
export type dispatchType = DT

const paramHandlers = {
  gradiant: (spec: unknown, param: string, newVal: unknown) => {
    if (param === 'maxValue' || param === 'minValue') {
      ;(spec as colorSpecGradiantNums)[param as keyof colorSpecGradiantNums] =
        Number(newVal)
    } else if (param === 'maxColor' || param === 'minColor') {
      ;(spec as colorSpecGradiantColors)[
        param as keyof colorSpecGradiantColors
      ] = newVal as fixedColor
    } else if (param === 'expr') {
      ;(spec as colorSpecGradiant).expr = newVal as string
    } else {
      console.error('changeSpec: Invalid param for gradiant', param)
    }
    return spec
  },
  numBucket: (spec: unknown, param: string, newVal: unknown) => {
    if (param === 'expr') {
      ;(spec as colorSpecNumBucket).expr = newVal as string
    } else if (param === 'color') {
      ;(spec as colorSpecNumBucket).color = newVal as fixedColor
    } else {
      console.error('changeSpec: Invalid param for numBucket', param)
    }
    return spec
  },
  strBucket: (spec: unknown, param: string, newVal: unknown) => {
    if (param == 'column') {
      ;(spec as colorSpecStrBucket).column = newVal as string
    } else if (param == 'value') {
      ;(spec as colorSpecStrBucket).value = newVal as string
    } else if (param == 'color') {
      ;(spec as colorSpecStrBucket).color = newVal as fixedColor
    } else {
      console.error('changeSpec: Invalid param for strBucket', param)
    }
    return spec
  },
}
export const storeSlice = createSlice({
  name: 'map',
  initialState: initialState,
  reducers: {
    updateFromAPI: (_, action) => {
      const newState = [...action.payload]
      for (let i = 0; i < newState.length; i += 1) {
        if (
          'deckOpts' in newState[i] &&
          typeof newState[i].deckOpts == 'string'
        ) {
          newState[i].deckOpts = JSON.parse(newState[i].deckOpts)
        }
      }
      return newState
    },
    toggleLoad: (state, action) => {
      const [urlname, isChecked] = action.payload
      for (let i = 0; i < state.length; i++) {
        if (state[i].urlname == urlname) {
          state[i].load = isChecked
          state[i].visible = isChecked
        }
      }
    },
    toggleVisible: (state, action) => {
      const [urlname, isChecked] = action.payload
      for (let i = 0; i < state.length; i++) {
        if (state[i].urlname == urlname) {
          state[i].visible = isChecked
          if (isChecked && !state[i].load) {
            state[i].load = true
          }
        }
      }
    },
    toggleConstant: (state, action) => {
      const [urlname, isChecked, element]: [string, boolean, string] =
        action.payload
      for (let i = 0; i < state.length; i++) {
        if (state[i].urlname != urlname) {
          continue
        }
        const deckOpts = state[i].deckOpts ?? ({} as deckOptsProps)
        const constants = deckOpts.constants ?? ({} as deckOptsConstants)
        const elemKey = element as keyof deckOptsConstantsBools
        constants[elemKey] = isChecked
        deckOpts.constants = constants
        state[i].deckOpts = deckOpts
      }
    },
    setNum: (state, action) => {
      const [urlname, newVal, element]: [string, number | string, string] =
        action.payload
      for (let i = 0; i < state.length; i++) {
        if (state[i].urlname != urlname) {
          continue
        }
        const deckOpts = state[i].deckOpts ?? ({} as deckOptsProps)
        const constants = deckOpts.constants ?? ({} as deckOptsConstants)
        const elemKey = element as keyof deckOptsConstantsNums
        constants[elemKey] = Number(newVal)
        deckOpts.constants = constants
        state[i].deckOpts = deckOpts
      }
    },
    changeColorType: (state, action) => {
      const [urlname, func, newVal, indx]: [string, string, string, number] =
        action.payload
      for (let i = 0; i < state.length; i++) {
        if (state[i].urlname != urlname) {
          continue
        }
        const deckOpts = state[i].deckOpts ?? ({} as deckOptsProps)
        const colors = deckOpts.colors ?? ({} as deckOptsColorsIn)

        if (!(func in colors)) {
          console.error(`changeColorType ${func} not in colors`, colors)
          return
        }
        const colorRules = colors[func as keyof deckOptsColorsIn] as colorSpec[]
        const newColorRules = [...colorRules]
        switch (newVal) {
          case 'gradiant':
            newColorRules[indx] = {
              gradiant: {
                maxValue: 100,
                minValue: 0,
                maxColor: [255, 255, 255],
                minColor: [0, 0, 0],
                expr: '',
              } as colorSpecGradiant,
            } as colorSpec
            break
          case 'numBucket':
            newColorRules[indx] = {
              numBucket: {
                expr: '',
                color: [255, 255, 255],
              } as colorSpecNumBucket,
            } as colorSpec
            break
          case 'strBucket':
            newColorRules[indx] = {
              strBucket: {
                column: '',
                value: '',
                color: [255, 255, 255],
              } as colorSpecStrBucket,
            } as colorSpec
            break
          default:
        }
        colors[func as keyof deckOptsColorsIn] = newColorRules
        deckOpts.colors = colors
        state[i].deckOpts = deckOpts
      }
    },
    changeSpec: (state, action) => {
      const [urlname, func, newVal, indx, param, spec]: [
        string,
        string,
        string | fixedColor,
        number,
        string,
        string,
      ] = action.payload
      for (let i = 0; i < state.length; i++) {
        if (state[i].urlname != urlname) {
          continue
        }
        const deckOpts = state[i].deckOpts ?? ({} as deckOptsProps)
        const colors = deckOpts.colors ?? ({} as deckOptsColorsIn)
        if (!(func in colors)) {
          console.error(`changeGradiantSpec ${func} not in colors`, colors)
          return
        }
        const colorRules = colors[func as keyof deckOptsColorsIn] as colorSpec[]
        const newColorRules = [...colorRules]
        if (!(spec in newColorRules[indx])) {
          console.error(`changeSpec ${spec} not in`, newColorRules, indx)
        }
        const oldColorRule = newColorRules[indx] as object
        let specData = oldColorRule[spec as keyof typeof oldColorRule] as object
        const paramhandle = paramHandlers[spec as keyof typeof paramHandlers]
        specData = paramhandle(specData, param, newVal) as typeof specData

        const newColorRule: Record<string, unknown> = {}
        newColorRule[spec as keyof typeof newColorRule] = specData

        colors[func as keyof deckOptsColorsIn] = newColorRules
        deckOpts.colors = colors
        state[i].deckOpts = deckOpts
      }
    },
    changeFixed: (state, action) => {
      const [urlname, func, newVal]: [string, string, fixedColor] =
        action.payload
      for (let i = 0; i < state.length; i++) {
        if (state[i].urlname != urlname) {
          continue
        }
        const deckOpts = state[i].deckOpts ?? ({} as deckOptsProps)
        const colors = deckOpts.colors ?? ({} as deckOptsColorsIn)
        colors[func as keyof deckOptsColorsIn] = newVal
        deckOpts.colors = colors
        state[i].deckOpts = deckOpts
      }
    },
    toggleFixedDyn: (state, action) => {
      const [urlname, func, newVal]: [string, string, string] = action.payload
      for (let i = 0; i < state.length; i++) {
        if (state[i].urlname != urlname) {
          continue
        }
        const deckOpts = state[i].deckOpts ?? ({} as deckOptsProps)
        const colors = deckOpts.colors ?? ({} as deckOptsColorsIn)
        if (newVal == 'Dynamic') {
          colors[func as keyof deckOptsColorsIn] = [
            {
              numBucket: {
                expr: '',
                color: [255, 255, 255],
              },
            } as colorSpec,
          ]
        } else if (newVal == 'Fixed') {
          colors[func as keyof deckOptsColorsIn] = [255, 255, 255]
        }
        deckOpts.colors = colors
        state[i].deckOpts = deckOpts
      }
    },
    addTranche: (state, action) => {
      const [urlname, func, indx, isUp]: [string, string, number, boolean] =
        action.payload
      for (let i = 0; i < state.length; i++) {
        if (state[i].urlname != urlname) {
          continue
        }
        const deckOpts = state[i].deckOpts ?? ({} as deckOptsProps)
        const colors = deckOpts.colors ?? ({} as deckOptsColorsIn)
        const colorRules = colors[func as keyof deckOptsColorsIn] as colorSpec[]
        const newColorRules = [...colorRules]
        const toCopy = newColorRules[indx]
        const insertIndx = isUp ? indx : indx + 1
        newColorRules.splice(insertIndx, 0, toCopy)
        colors[func as keyof deckOptsColorsIn] = newColorRules
        deckOpts.colors = colors
        state[i].deckOpts = deckOpts
      }
    },
    removeTranche: (state, action) => {
      const [urlname, func, indx]: [string, string, number, boolean] =
        action.payload
      for (let i = 0; i < state.length; i++) {
        if (state[i].urlname != urlname) {
          continue
        }
        const deckOpts = state[i].deckOpts ?? ({} as deckOptsProps)
        const colors = deckOpts.colors ?? ({} as deckOptsColorsIn)
        const colorRules = colors[func as keyof deckOptsColorsIn] as colorSpec[]
        const newColorRules = colorRules
          .slice(0, indx)
          .concat(colorRules.slice(indx + 1))
        colors[func as keyof deckOptsColorsIn] = newColorRules
        deckOpts.colors = colors
        state[i].deckOpts = deckOpts
      }
    },
    changeView: (state, action) => {
      const [urlname, load, visible, viewname, deckOpts]: [
        string,
        boolean,
        boolean,
        string,
        deckOptsProps,
      ] = action.payload
      let _blah = load
      _blah = visible
      _blah = false
      if (_blah) {
        console.log('impossible')
      }

      for (let i = 0; i < state.length; i++) {
        if (state[i].urlname != urlname) {
          continue
        }
        state[i].deckOpts = deckOpts
        // state[i].load = load
        // state[i].visible = visible
        state[i].viewname = viewname
      }
    },
  },
})

export const changeViewFetch = async (
  dispatch: DT,
  urlname: string,
  viewname: string
) => {
  fetch(`/api/newmap/get_view?urlname=${urlname}&viewname=${viewname}`)
    .then((resp) => resp.json())
    .then((data) => {
      const { load, visible, viewname, deckOpts } = data[0]
      const fixOpts = (
        typeof deckOpts == 'string' ? JSON.parse(deckOpts) : deckOpts
      ) as deckOptsProps
      console.log('change view', urlname, viewname, fixOpts)
      dispatch(changeView([urlname, load, visible, viewname, fixOpts]))
    })
}
// Action creators are generated for each case reducer function
export const {
  updateFromAPI,
  toggleLoad,
  toggleVisible,
  toggleConstant,
  setNum,
  changeColorType,
  changeSpec,
  changeFixed,
  toggleFixedDyn,
  addTranche,
  removeTranche,
  changeView,
} = storeSlice.actions

export default storeSlice.reducer
